import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@app/shared/components/icon/icon.component';
import { AuthService } from '@auth/auth.service';
import { UserService } from '@services/user.service';
import { AddressNumberService } from '@services/address-number.service';
import { I18nService } from '@shared/services/i18n.service';
import { EnvironmentService } from '@core/services/environment.service';
import { HttpErrorStateService } from '@core/services/http-error-state.service';
import { Subscription } from 'rxjs';
import { ROUTE_PATHS } from '@app/app.routes';

@Component({
	selector: 'app-header',
	imports: [CommonModule, FormsModule, RouterModule, TranslateModule, IconComponent],
	standalone: true,
	templateUrl: './header.component.html',
	styleUrl: './header.component.scss',
})
export class HeaderComponent implements AfterViewInit, OnInit, OnDestroy {
	private readonly cdr = inject(ChangeDetectorRef);

	// Menu states
	isUserMenuOpen = false;
	isLanguageMenuOpen = false;
	isMobileMenuOpen = false;
	isMobileLangOpen = false;
	isExtraLinksMenuOpen = false;
	isAddressNumberMenuOpen = false;
	addressNumberFilter = '';

	// Injected services
	public authService = inject(AuthService);
	public translateService = inject(TranslateService);
	public i18nService = inject(I18nService);
	public userService = inject(UserService);
	public addressNumberService = inject(AddressNumberService);
	private readonly router = inject(Router);
	public environmentService = inject(EnvironmentService);
	public errorState = inject(HttpErrorStateService);

	private authSub?: Subscription;

	ngOnInit(): void {
		this.authSub = this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
			if (isAuthenticated) {
				this.addressNumberService.loadAddressNumbers().subscribe();
			}
		});
	}

	ngOnDestroy(): void {
		this.authSub?.unsubscribe();
	}

	ngAfterViewInit(): void {
		this.cdr.markForCheck();
	}

	/**
	 * Toggle user menu dropdown
	 */
	toggleUserMenu(): void {
		this.isUserMenuOpen = !this.isUserMenuOpen;
		// Close other menus
		this.isLanguageMenuOpen = false;
	}

	/**
	 * Toggle language menu dropdown
	 */
	toggleLanguageMenu(): void {
		this.isLanguageMenuOpen = !this.isLanguageMenuOpen;
		// Close other menus
		this.isUserMenuOpen = false;
	}

	/**
	 * Toggle mobile menu
	 */
	toggleMobileMenu(): void {
		this.isMobileMenuOpen = !this.isMobileMenuOpen;
	}

	/**
	 * Close mobile menu if it's open
	 */
	closeMobileMenuIfOpen(): void {
		if (this.isMobileMenuOpen) {
			this.isMobileMenuOpen = false;
		}
	}

	/**
	 * Toggle extra links dropdown
	 */
	toggleExtraLinksMenu(): void {
		this.isExtraLinksMenuOpen = !this.isExtraLinksMenuOpen;
		// Close other menus
		this.isUserMenuOpen = false;
		this.isLanguageMenuOpen = false;
		this.isAddressNumberMenuOpen = false;
	}

	/**
	 * Toggle address number selector dropdown
	 */
	toggleAddressNumberMenu(): void {
		this.isAddressNumberMenuOpen = !this.isAddressNumberMenuOpen;
		if (this.isAddressNumberMenuOpen) this.addressNumberFilter = '';
		// Close other menus
		this.isUserMenuOpen = false;
		this.isLanguageMenuOpen = false;
	}

	get filteredAddressNumbers() {
		const term = this.addressNumberFilter.toLowerCase();
		if (!term) return this.addressNumberService.addressNumbers;
		return this.addressNumberService.addressNumbers.filter(
			(e) => e.addressNumber?.toLowerCase().includes(term) || e.businessUnit?.toLowerCase().includes(term),
		);
	}

	/**
	 * Select an address number
	 */
	selectAddressNumber(addressNumber: string): void {
		this.addressNumberService.select(addressNumber);
		this.isAddressNumberMenuOpen = false;
	}

	/**
	 * Close extra links menu and navigate
	 */
	selectExtraLink(): void {
		this.isExtraLinksMenuOpen = false;
	}

	/**
	 * Get the current active link text based on the router state
	 */
	getCurrentActiveLink(): string {
		const currentUrl = this.router.url;

		// Remove query parameters and fragments for cleaner matching
		const cleanUrl = currentUrl.split('?')[0].split('#')[0];

		if (cleanUrl === '/dashboard' || cleanUrl === '/' || cleanUrl === '') {
			return this.translateService.instant('APP.DASHBOARD');
		} else if (cleanUrl === '/user-profile') {
			return this.translateService.instant('APP.USER_PROFILE');
		} else if (cleanUrl === '/api-credentials') {
			return this.translateService.instant('APP.API_CREDENTIALS');
		} else if (cleanUrl === '/digital-services') {
			return this.translateService.instant('APP.DIGITALSERVICES');
		} else {
			// For sub-routes, check if URL contains the main route
			if (currentUrl.includes('/user-profile')) {
				return this.translateService.instant('APP.USER_PROFILE');
			} else if (currentUrl.includes('/api-credentials')) {
				return this.translateService.instant('APP.API_CREDENTIALS');
			} else if (currentUrl.includes('/digital-services')) {
				return this.translateService.instant('APP.DIGITALSERVICES');
			} else if (currentUrl.includes('/dashboard')) {
				return this.translateService.instant('APP.DASHBOARD');
			} else {
				// Default fallback to dashboard
				return this.translateService.instant('APP.DASHBOARD');
			}
		}
	}

	/**
	 * Switch application language
	 */
	switchLanguage(lang: string): void {
		this.i18nService.changeLanguage(lang);
	}

	/**
	 * Login user
	 */
	login(): void {
		this.authService.login();
	}

	/**
	 * Logout user
	 */
	logout(): void {
		this.authService.logout();
	}

	/**
	 * Handle clicks outside header menus to close them
	 */
	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		const clickedElement = event.target as HTMLElement;

		// Check if the click is on dropdown toggle buttons (don't close in that case)
		const isClickOnToggle =
			clickedElement.closest('.weHeader__barToolsButton') ||
			clickedElement.closest('.weHeader__mobileLangToggle') ||
			clickedElement.closest('.weHeader__mobileDropdownToggle') ||
			clickedElement.closest('.weHeader__addressNumberToggle');
		if (isClickOnToggle) {
			return;
		}

		// Check which flyout was clicked (if any)
		const clickedFlyout = clickedElement.closest('.weUtilsFlyout');

		// Close header bar flyouts if clicking outside them
		if (!clickedFlyout || !clickedFlyout.classList.contains('weHeader__barFlyout')) {
			this.isUserMenuOpen = false;
			this.isLanguageMenuOpen = false;
			this.isAddressNumberMenuOpen = false;
		}

		// Close full-width dropdown if clicking outside it
		const clickedFullWidthDropdown =
			clickedElement.closest('.weHeader__fullWidthDropdown') || clickedElement.closest('.weHeader__mobileDropdown');
		if (!clickedFullWidthDropdown) {
			this.isExtraLinksMenuOpen = false;
		}
	}

	navigateToProfile(): void {
		void this.router.navigate(['/user-profile']);
	}

	shouldShowSubHeader(): boolean {
		const currentUrl = this.router.url.split('?')[0].split('#')[0];

		const hiddenRoutes = [ROUTE_PATHS.REGISTRATION_FORM, ROUTE_PATHS.REGISTRATION_FORM_FALLBACK].map((route) =>
			route.startsWith('/') ? route : `/${route}`,
		);

		return !hiddenRoutes.some((route) => currentUrl.startsWith(route));
	}
}
